import { ApiError, UnauthenticatedError } from '@lib/api';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
    archiveReleaseComponent,
    createReleaseComponent,
    getReleaseComponent,
    listReleaseComponents,
    updateReleaseComponent,
    type CreateReleaseInput,
} from '../api/release-components';

import type { UpdateReleaseInput } from '../api/release-edit';

/**
 * Server state for release components.
 *
 * Its own key space, apart from `componentKeys`: a release is a separate
 * aggregate on a separate route, and `GET /components` is never to be refetched
 * or shaped by it. Same posture as `use-components.ts` otherwise: nothing
 * optimistic, creates never retried, `networkMode: 'always'` so a failure
 * surfaces rather than parking the query as paused.
 */
export const releaseKeys = {
    all: ['release-components'] as const,
    list: () => [...releaseKeys.all, 'list'] as const,
    detail: (id: string) => [...releaseKeys.all, 'detail', id] as const,
};

function retryUnlessClientError(failureCount: number, error: Error): boolean {
    if (error instanceof UnauthenticatedError) return false;
    if (error instanceof ApiError && error.status < 500) return false;
    return failureCount < 1;
}

export function useReleaseComponents() {
    return useQuery({
        queryKey: releaseKeys.list(),
        queryFn: ({ signal }) => listReleaseComponents(signal),
        retry: retryUnlessClientError,
        networkMode: 'always',
    });
}

/** One release. A 404 (unknown or archived id) is an answer, so it is not retried. */
export function useReleaseComponent(id: string) {
    return useQuery({
        queryKey: releaseKeys.detail(id),
        queryFn: ({ signal }) => getReleaseComponent(id, signal),
        enabled: id.length > 0,
        retry: retryUnlessClientError,
        networkMode: 'always',
    });
}

/** Create a release, then refetch the list; the invalidation is returned so the dialog waits for it. */
export function useCreateRelease() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (input: CreateReleaseInput) => createReleaseComponent(input),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: releaseKeys.list() }),
        retry: false,
        networkMode: 'always',
    });
}

/** Edit version and flags, then refetch this release and the list (its version label shows there). */
export function useUpdateRelease(id: string) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (input: UpdateReleaseInput) => updateReleaseComponent(id, input),
        onSuccess: () =>
            Promise.all([
                queryClient.invalidateQueries({ queryKey: releaseKeys.detail(id) }),
                queryClient.invalidateQueries({ queryKey: releaseKeys.list() }),
            ]),
        retry: false,
        networkMode: 'always',
    });
}

/**
 * Archive. The detail is dropped rather than refetched: an archived release is a
 * 404 to readers, so a refetch would only turn into a not-found panel.
 */
export function useArchiveRelease(id: string) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (input: { readonly expectedRowVersion: number; readonly reason?: string }) =>
            archiveReleaseComponent(id, input),
        onSuccess: () => {
            queryClient.removeQueries({ queryKey: releaseKeys.detail(id) });
            return queryClient.invalidateQueries({ queryKey: releaseKeys.list() });
        },
        retry: false,
        networkMode: 'always',
    });
}
